import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {startWith, tap} from 'rxjs/operators';
import {RequestCache} from '../models/request-cache';

/**
 * An http interceptor that caches requests having the appropriate headers.
 * The headers can be added using the static #createCacheHeader method. Only get requests can be cached.
 */
@Injectable()
export class CachingInterceptor implements HttpInterceptor {
    private static readonly CACHE_HEADER = 'x-cache';
    private static readonly REFRESH_HEADER = 'x-refresh';
    private static readonly MAX_AGE_HEADER = 'x-cache-max-age';

    constructor(private cache: RequestCache) {
    }

    /**
     * Creates the header fields that are needed to use this cache interceptor.
     *
     * @param refresh true, if the cache entry shall be renewed, even if it exist and is still valid.
     * @param maxAge max age of the cache entry in milisecond. Afterwards it will be removed from the cache.
     */
    public static createCacheHeader(refresh: boolean = null, maxAge: number = null) {
        const header = {};

        header[CachingInterceptor.CACHE_HEADER] = 'true';
        if (refresh != null && refresh) {
            header[CachingInterceptor.REFRESH_HEADER] = 'true';
        }
        if (maxAge != null) {
            header[CachingInterceptor.MAX_AGE_HEADER] = String(maxAge);
        }

        return header;
    }

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        if (!this.isCachable(req)) {
            // continue if not cachable.
            return next.handle(req);
        }

        const maxAge = req.headers.get(CachingInterceptor.MAX_AGE_HEADER);
        let cachedResponse;
        if (maxAge != null && !isNaN(Number(maxAge))) {
            cachedResponse = this.cache.get(req, Number(maxAge));
        } else {
            cachedResponse = this.cache.get(req, RequestCache.DEFAULT_MAX_AGE);
        }

        if (req.headers.get(CachingInterceptor.REFRESH_HEADER)) {
            // use cache then refresh
            const results$ = this.sendRequest(req, next, this.cache);
            return cachedResponse ?
                results$.pipe(startWith(cachedResponse)) :
                results$;
        }

        // use cache or fetch data.
        return cachedResponse ?
            of(cachedResponse) : this.sendRequest(req, next, this.cache);
    }

    /**
     * Is this request cachable?
     */
    private isCachable(req: HttpRequest<any>): boolean {
        // Only GET requests are cachable. They need to have an x-cache header with the value true
        return req.method === 'GET' && req.headers.get(CachingInterceptor.CACHE_HEADER) === 'true';
    }

    /**
     * Get server response observable by sending request to `next()`.
     * Will add the response to the cache on the way out.
     */
    private sendRequest(
        req: HttpRequest<any>,
        next: HttpHandler,
        cache: RequestCache): Observable<HttpEvent<any>> {
        // remove the caching header because own headers are not allowed always.
        req = req.clone({
            headers: req.headers
                .delete(CachingInterceptor.CACHE_HEADER)
                .delete(CachingInterceptor.REFRESH_HEADER)
                .delete(CachingInterceptor.MAX_AGE_HEADER)
        });

        return next.handle(req).pipe(
            tap(event => {
                // There may be other events besides the response.
                if (!(event instanceof HttpResponse)) {
                    return;
                }

                // Update the cache.
                cache.put(req, event);
            })
        );
    }
}
